import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import Icon from '../Icon';

interface ComparisonCard {
  name: string;
  icon: string;
  goodAt: string;
  failsAt: string;
  ourApproach: string;
  color: string;
}

const comparisons: ComparisonCard[] = [
  {
    name: 'Redis Streams',
    icon: 'stream',
    goodAt: 'Blazing fast pub/sub and caching. Great for leaderboards and session storage.',
    failsAt: 'No per-auction isolation. Ordering across streams is complex. State must be reconstructed externally.',
    ourApproach: 'Each auction is a Durable Object with its own mailbox. State lives inside the actor, so no external reconstruction is needed.',
    color: 'red',
  },
  {
    name: 'Postgres Row-Locking',
    icon: 'table_rows',
    goodAt: 'ACID transactions, proven reliability. Perfect for banking and e-commerce.',
    failsAt: 'Serialized writes = contention under load. 100 concurrent bids = 99 waiting. Latency spikes during bid wars.',
    ourApproach: 'Actors process one message at a time, but each auction has its own actor. 1,000 auctions = 1,000 parallel queues.',
    color: 'blue',
  },
  {
    name: 'Kafka + Microservices',
    icon: 'hub',
    goodAt: 'Massive throughput, event replay, decoupled services. Great for analytics pipelines.',
    failsAt: 'Async by design. Consumer lag breaks real-time UX. Partitioning per auction = operational complexity.',
    ourApproach: 'WebSockets deliver updates in ~20ms. No consumer lag. Hibernation keeps connections alive without burning CPU.',
    color: 'green',
  },
  {
    name: 'DynamoDB Conditional Writes',
    icon: 'cloud',
    goodAt: 'Serverless, scalable, pay-per-use. Conditional updates prevent overwrites.',
    failsAt: 'Retries on conflict. Under bid wars, conditional failures cascade. No built-in real-time push.',
    ourApproach: 'No retries needed. The actor processes bids in order. If your bid is too low, you know immediately.',
    color: 'amber',
  },
];

const colorClasses: Record<string, { border: string; icon: string; badge: string }> = {
  red: { border: 'border-red-200', icon: 'text-red-500', badge: 'bg-red-100 text-red-700' },
  blue: { border: 'border-blue-200', icon: 'text-blue-500', badge: 'bg-blue-100 text-blue-700' },
  green: { border: 'border-green-200', icon: 'text-green-500', badge: 'bg-green-100 text-green-700' },
  amber: { border: 'border-amber-200', icon: 'text-amber-500', badge: 'bg-amber-100 text-amber-700' },
};

export default function ComparisonSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <section ref={ref} className="py-12 px-4 bg-gray-50">
      <div className="max-w-5xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <span className="inline-flex items-center gap-2 bg-gray-200 text-gray-700 text-sm font-medium px-3 py-1 rounded-full mb-4">
            <Icon name="compare" size="small" />
            Honest Comparisons
          </span>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            Why Not Use…?
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Every technology has trade-offs. Here's why we chose the Actor Model.
          </p>
        </motion.div>

        {/* Comparison Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {comparisons.map((card, i) => {
            const colors = colorClasses[card.color];
            return (
              <motion.div
                key={card.name}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.1 + i * 0.15 }}
                className={`bg-white ${colors.border} border rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow`}
              >
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-lg ${colors.badge} flex items-center justify-center`}>
                    <Icon name={card.icon} className={colors.icon} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{card.name}</h3>
                </div>

                {/* Good At */}
                <div className="mb-3">
                  <div className="flex items-center gap-1 text-xs font-medium text-green-600 uppercase tracking-wide mb-1">
                    <Icon name="thumb_up" size="small" />
                    Good At
                  </div>
                  <p className="text-sm text-gray-600">{card.goodAt}</p>
                </div>

                {/* Fails At */}
                <div className="mb-3">
                  <div className="flex items-center gap-1 text-xs font-medium text-red-600 uppercase tracking-wide mb-1">
                    <Icon name="thumb_down" size="small" />
                    Fails for Real-Time Auctions
                  </div>
                  <p className="text-sm text-gray-600">{card.failsAt}</p>
                </div>

                {/* Our Approach */}
                <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                  <div className="flex items-center gap-1 text-xs font-medium text-purple-700 uppercase tracking-wide mb-1">
                    <Icon name="lightbulb" size="small" />
                    Our Approach
                  </div>
                  <p className="text-sm text-purple-900">{card.ourApproach}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
